import { gsap, ScrollTrigger, registerTimeline } from '../scroll_animation'

registerTimeline('home', (root, container) => {
  const stage2 = root.querySelector('[data-scroll-stage="2"]')
  const stage3 = root.querySelector('[data-scroll-stage="3"]')
  const stage4 = root.querySelector('[data-scroll-stage="4"]')
  const stage5 = root.querySelector('[data-scroll-stage="5"]')
  const stage6 = root.querySelector('[data-scroll-stage="6"]')
  const stage7 = root.querySelector('[data-scroll-stage="7"]')
  const stage8 = root.querySelector('[data-scroll-stage="8"]')
  const stage9 = root.querySelector('[data-scroll-stage="9"]')

  const title = root.querySelector('[data-scroll-title]')
  const subtitle = root.querySelector('[data-scroll-subtitle]')
  const arrow = root.querySelector('[data-scroll-arrow]')
  const arrowInner = root.querySelector('[data-scroll-arrow-inner]')
  const arrowLine = root.querySelector('[data-scroll-arrow-line]')
  const cardOverlay = root.querySelector('[data-scroll-card]')
  const cardMain = root.querySelector('[data-scroll-card-main]')
  const cardInner = root.querySelector('[data-scroll-card-inner]')
  const circleContainer = root.querySelector('[data-scroll-circle]')
  const circleCards = root.querySelectorAll('[data-scroll-circle-card]')
  const stageText = root.querySelector('[data-scroll-stage-text]')
  const splitOverlay = root.querySelector('[data-scroll-split]')
  const contentRight = root.querySelector('[data-scroll-content-right]')
  const contentLeft = root.querySelector('[data-scroll-content-left]')

  const vh = window.innerHeight
  const vw = window.innerWidth
  // const CIRCLE_RADIUS = Math.min(vw * 0.38, vh * 0.38)
  const CIRCLE_RADIUS = Math.max(280, Math.min(Math.min(vw, vh) * 0.7, 450))
  const CARD_COUNT = circleCards.length

  // Pre-compute random rotations (must be stable across scrub)
  const pileRotations = Array.from({ length: CARD_COUNT }, (_, i) => -3 + (i % 7) - 3)
  const pileRotations2 = Array.from({ length: CARD_COUNT }, (_, i) => 2 + (i % 5) - 2)

  // Initial state: circle cards hidden at origin
  circleCards.forEach(card => {
    gsap.set(card, { opacity: 0, x: 0, y: 0, rotation: 0, scale: 0.45 })
  })

  // --- Stage 2: Title/subtitle fly up, arrow fades, card emerges ---
  const tl2 = gsap.timeline({
    scrollTrigger: { trigger: stage2, start: 'top top', end: 'bottom top', scrub: true }
  })

  tl2.to(title, { yPercent: -300, opacity: 0, duration: 0.3 }, 0)
  tl2.to(subtitle, { yPercent: -400, opacity: 0, duration: 0.3 }, 0)
  // Arrow stays visible — no longer faded out here
  tl2.fromTo(arrowLine, { scaleY: 0 }, { scaleY: 1, duration: 0.5 }, 0)
  tl2.fromTo(cardOverlay, { opacity: 0 }, { opacity: 1, duration: 0.4 }, 0.1)
  tl2.fromTo(cardMain, { scale: 0.3 }, { scale: 1, duration: 0.6 }, 0.1)

  // --- Stage 3: Card flips from back (180°) to front (0°) ---
  const tl3 = gsap.timeline({
    scrollTrigger: { trigger: stage3, start: 'top top', end: 'bottom top', scrub: true }
  })

  tl3.to(arrowLine, { opacity: 0, duration: 0.3 }, 0)
  tl3.fromTo(cardInner, { rotateY: 180 }, { rotateY: 0, duration: 1, ease: 'none' }, 0)

  // Idle spin: autonomous rotation via CSS `rotate` (composes with GSAP's `transform`)
  let idleActive = false
  let idleRotation = 0
  gsap.ticker.add((time, deltaTime) => {
    if (!idleActive) return
    idleRotation += deltaTime * 0.004 // ~18°/sec ≈ 20s per revolution
    circleContainer.style.rotate = `${idleRotation}deg`
  })

  function startIdleSpin() { idleActive = true }
  function stopIdleSpin() {
    idleActive = false
    idleRotation = 0
    circleContainer.style.rotate = ''
  }

  // --- Stage 4: Main card shrinks, circle cards fan out, CTA text appears ---
  const tl4 = gsap.timeline({
    scrollTrigger: {
      trigger: stage4, start: 'top top', end: '+=200%', scrub: true,
      onEnterBack: stopIdleSpin,
    }
  })

  // Main card shrinks and fades
  tl4.to(cardMain, { scale: 0, opacity: 0, duration: 0.3 }, 0)

  // Circle cards fan out into a circle
  circleCards.forEach((card, i) => {
    const angle = (i / CARD_COUNT) * Math.PI * 2 - Math.PI / 2
    const x = Math.cos(angle) * CIRCLE_RADIUS
    const y = Math.sin(angle) * CIRCLE_RADIUS
    const rot = (angle * 180 / Math.PI) + 90

    tl4.to(card, {
      opacity: 1,
      x: x,
      y: y,
      rotation: rot,
      scale: 0.8,
      duration: 0.5,
    }, 0.05 + i * 0.02)
  })

  // Start idle spin as soon as fan-out completes
  tl4.call(startIdleSpin, [], 0.8)

  // CTA text fades in at center
  tl4.to(stageText, { opacity: 1, duration: 0.3 }, 0.5)

  // --- Stage 5: Circle spins further, cards collect to left pile, content flies in right ---
  const tl5 = gsap.timeline({
    scrollTrigger: {
      trigger: stage5, start: 'top top', end: '+=300%', scrub: true,
      onEnter: stopIdleSpin,
      onLeaveBack: startIdleSpin,
    }
  })

  // Fade out CTA text
  tl5.to(stageText, { opacity: 0, duration: 0.1 }, 0)

  // Show split overlay
  tl5.to(splitOverlay, { opacity: 1, duration: 0.05 }, 0)

  // Continue spinning in the same direction so there is no reversal
  tl5.fromTo(circleContainer, { rotation: 360 }, { rotation: 720, duration: 2, ease: 'power3.inOut' }, 0)

  // Cards collect to left pile (stable rotations)
  const pileLeftX = -vw * 0.25
  circleCards.forEach((card, i) => {
    tl5.to(card, {
      x: pileLeftX,
      y: i * -2,
      rotation: pileRotations[i],
      scale: 0.675,
      duration: 0.25,
    }, 0.1 + i * 0.015)
  })

  // Content right: fly in from below, rest longer, fly out up
  tl5.fromTo(contentRight, { opacity: 0, y: 300 }, { opacity: 1, y: 0, duration: 0.2 }, 0.25)
  tl5.to(contentRight, { y: -300, opacity: 0, duration: 0.15 }, 0.85)

  // --- Stage 6: Cards flip 3D from left to right pile, content flies in left ---
  const tl6 = gsap.timeline({
    scrollTrigger: { trigger: stage6, start: 'top top', end: 'bottom top', scrub: true }
  })

  // Cards flip from left to right in reverse order (top of pile first)
  const pileRightX = vw * 0.25
  circleCards.forEach((card, i) => {
    const cardInnerEl = card.querySelector('.landing-card-inner')
    const ri = CARD_COUNT - 1 - i

    // zIndex changes early in the flight
    tl6.to(card, { zIndex: ri + 1, duration: 0.08 }, 0.02 + ri * 0.02)

    tl6.to(card, {
      x: pileRightX,
      y: ri * -2,
      rotation: pileRotations2[i],
      duration: 0.25,
    }, 0.02 + ri * 0.02)

    if (cardInnerEl) {
      tl6.fromTo(cardInnerEl,
        { rotateY: 0 },
        { rotateY: 180, duration: 0.25 },
        0.02 + ri * 0.02
      )
    }
  })

  // Content left: fly in from below, rest, fly out up
  tl6.fromTo(contentLeft, { opacity: 0, y: 300 }, { opacity: 1, y: 0, duration: 0.15 }, 0.1)
  tl6.to(contentLeft, { y: -300, opacity: 0, duration: 0.15 }, 0.55)

  // Fade out everything well before the end
  tl6.to(cardOverlay, { opacity: 0, duration: 0.1 }, 0.7)
  tl6.to(splitOverlay, { opacity: 0, duration: 0.1 }, 0.7)

  // --- Stages 7-8: Content sections fade in/out ---
  const stageContentPairs = [
    [stage7, root.querySelector('[data-scroll-stage7-content]')],
    [stage8, root.querySelector('[data-scroll-stage8-content]')],
  ]

  stageContentPairs.forEach(([stage, content]) => {
    if (!stage || !content) return
    const tl = gsap.timeline({
      scrollTrigger: { trigger: stage, start: 'top top', end: 'bottom top', scrub: true }
    })
    tl.fromTo(content, { opacity: 0, y: 200 }, { opacity: 1, y: 0, duration: 0.3 }, 0)
    tl.to(content, { opacity: 0, y: -200, duration: 0.3 }, 0.7)
  })

  // --- Stage 9: Torus snake animation + content ---
  const stage9Content = root.querySelector('[data-scroll-stage9-content]')
  const MAJOR_R = Math.min(vw * 0.8, 500)
  const TORUS_TILT = 0.25

  let torusActive = false
  let torusPhase = 0

  function torusPos(u) {
    const x = MAJOR_R * Math.cos(u)
    const y = MAJOR_R * Math.sin(u) * TORUS_TILT
    const depth = Math.sin(u)
    return { x, y, depth }
  }
  const TORUS_Y_OFFSET = -vh * 0.15

  gsap.ticker.add((time, deltaTime) => {
    if (!torusActive) return
    torusPhase -= deltaTime * 0.000194

    circleCards.forEach((card, i) => {
      const u = torusPhase + (i / CARD_COUNT) * Math.PI * 2
      const pos = torusPos(u)
      const scale = 0.6 + (pos.depth + 1) * 0.08
      const cardInnerEl = card.querySelector('.landing-card-inner')

      gsap.set(card, {
        x: pos.x,
        y: pos.y + TORUS_Y_OFFSET,
        rotation: 0,
        scale: scale,
        zIndex: Math.round((pos.depth + 1) * 100),
      })

      if (cardInnerEl) {
        gsap.set(cardInnerEl, { rotateY: 90 - u * 180 / Math.PI })
      }
    })
  })

  function startTorusOrbit() { torusActive = true }
  function stopTorusOrbit() { torusActive = false; torusPhase = 0 }

  let savedCardStates = []
  let savedOverlayState = {}

  function saveCardStates() {
    savedOverlayState = {
      cardOverlayOpacity: gsap.getProperty(cardOverlay, 'opacity'),
      splitOverlayOpacity: gsap.getProperty(splitOverlay, 'opacity'),
    }
    savedCardStates = circleCards.map(card => {
      const inner = card.querySelector('.landing-card-inner')
      return {
        x: gsap.getProperty(card, 'x'),
        y: gsap.getProperty(card, 'y'),
        rotation: gsap.getProperty(card, 'rotation'),
        scale: gsap.getProperty(card, 'scale'),
        opacity: gsap.getProperty(card, 'opacity'),
        zIndex: gsap.getProperty(card, 'zIndex'),
        innerRotateY: inner ? gsap.getProperty(inner, 'rotateY') : 0,
      }
    })
  }

  function restoreCardStates() {
    gsap.set(cardOverlay, { opacity: savedOverlayState.cardOverlayOpacity ?? 0 })
    gsap.set(splitOverlay, { opacity: savedOverlayState.splitOverlayOpacity ?? 0 })
    circleCards.forEach((card, i) => {
      const state = savedCardStates[i]
      if (!state) return
      gsap.set(card, {
        x: state.x, y: state.y, rotation: state.rotation,
        scale: state.scale, opacity: state.opacity, zIndex: state.zIndex,
      })
      const inner = card.querySelector('.landing-card-inner')
      if (inner) gsap.set(inner, { rotateY: state.innerRotateY })
    })
  }

  const tl9 = gsap.timeline({
    scrollTrigger: {
      trigger: stage9, start: 'top top', end: 'bottom bottom', scrub: true,
      onEnter: saveCardStates,
      onEnterBack: stopTorusOrbit,
      onLeaveBack: restoreCardStates,
    }
  })

  // Bring card overlay back
  tl9.fromTo(cardOverlay, { opacity: 0 }, { opacity: 1, duration: 0.2, immediateRender: false }, 0)
  tl9.set(circleContainer, { rotation: 0 }, 0)

  // Reset all cards to hidden before fly-in
  circleCards.forEach(card => {
    tl9.set(card, { opacity: 0, x: 0, y: TORUS_Y_OFFSET, scale: 0.2, rotation: 0 }, 0)
    const inner = card.querySelector('.landing-card-inner')
    if (inner) tl9.set(inner, { rotateY: 0 }, 0)
  })

  // Fly cards from center into their initial torus positions
  circleCards.forEach((card, i) => {
    const u = (i / CARD_COUNT) * Math.PI * 2
    const pos = torusPos(u)
    const scale = 0.4 + (pos.depth + 1) * 0.08
    const cardInnerEl = card.querySelector('.landing-card-inner')

    tl9.to(card, {
      x: pos.x, y: pos.y + TORUS_Y_OFFSET,
      rotation: 0,
      scale: scale,
      opacity: 1,
      zIndex: Math.round((pos.depth + 1) * 100),
      duration: 0.4,
      immediateRender: false,
    }, 0.1 + i * 0.015)

    if (cardInnerEl) {
      tl9.to(cardInnerEl, {
        rotateY: 90 - u * 180 / Math.PI,
        duration: 0.4,
        immediateRender: false,
      }, 0.1 + i * 0.015)
    }
  })

  // Start orbit once fly-in completes
  tl9.call(startTorusOrbit, [], 0.7)

  // Fade in stage 9 text content
  if (stage9Content) {
    tl9.fromTo(stage9Content, { opacity: 0, y: 200 }, { opacity: 1, y: 0, duration: 0.3 }, 0.3)
  }

  // --- Scroll arrow: 3D spin, direction flip, idle bounce ---
  let arrowYSpin = 0
  let scrollTimer = null
  let flippedUp = false

  // Idle bounce animation
  const idleBounce = gsap.to(arrowInner, {
    y: -8, duration: 0.8, repeat: -1, yoyo: true, ease: 'sine.inOut',
  })

  function flipArrow(up) {
    if (flippedUp === up) return
    flippedUp = up
    gsap.to(arrowInner, { scaleY: up ? -1 : 1, duration: 0.3, ease: 'power2.out' })
  }

  // Scroll direction + Y-axis spin
  let lastScroll = container.scrollTop
  const maxScroll = () => container.scrollHeight - container.clientHeight

  gsap.ticker.add(() => {
    const currentScroll = container.scrollTop
    const delta = currentScroll - lastScroll
    lastScroll = currentScroll

    // Flip logic: up at bottom, down at top
    if (currentScroll >= maxScroll() - 2) flipArrow(true)
    else if (currentScroll <= 2) flipArrow(false)

    if (Math.abs(delta) > 0.5) {
      clearTimeout(scrollTimer)
      scrollTimer = setTimeout(() => {
        // Ease spin back to nearest full rotation
        gsap.to(arrowInner, { rotateY: 0, duration: 0.8, ease: 'power2.out' })
        // Resume bounce without jitter: reset y first, then restart
        gsap.to(arrowInner, {
          y: 0, duration: 0.3, ease: 'power2.out',
          onComplete: () => { idleBounce.restart() },
        })
      }, 200)

      // Spin on Y-axis while scrolling (slow)
      arrowYSpin += delta * 0.3
      gsap.set(arrowInner, { rotateY: arrowYSpin % 360 })

      // Pause bounce while scrolling
      if (idleBounce.isActive()) {
        idleBounce.pause()
        gsap.to(arrowInner, { y: 0, duration: 0.15 })
      }
    }
  })

  // Click to scroll: GSAP-driven smooth scroll, interruptible by user
  let clickTween = null

  function killClickScroll() {
    if (clickTween) { clickTween.kill(); clickTween = null }
  }

  // User scroll interrupts click-scroll
  container.addEventListener('wheel', killClickScroll, { passive: true })
  container.addEventListener('touchstart', killClickScroll, { passive: true })

  arrow.addEventListener('click', () => {
    if (clickTween && clickTween.isActive()) return
    const start = container.scrollTop
    const target = flippedUp
      ? Math.max(0, start - vh)
      : Math.min(maxScroll(), start + vh)
    const proxy = { scroll: start }
    clickTween = gsap.to(proxy, {
      scroll: target,
      duration: 0.8,
      ease: 'power2.inOut',
      onUpdate: () => container.scrollTo({ top: proxy.scroll }),
    })
  })
})
